import analytics from '@/shared-utils/analytics'
import { resizeAndOptimizeImage } from '@/shared-utils/resizeAndOptimizeImage'
import toggleFavoriteHotel from '@/shared-utils/toggleFavoriteHotel'
import type { FavoriteHotel } from '@/types'
import { CarsFill, FlightsFill, HotelsFill } from '@pcln/brand'
import { isAfter, isToday } from 'date-fns'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import { parseISO } from 'date-fns/parseISO'
import type { TripGroupingApiResponse } from '../TripGrouping/types'
import { FLY_CABIN_CLASS } from './constants'
import fireRecentSearchCardClickEvent, { fireViewAllButtonClickEvent } from './ga4'
import type {
  AbandonedCarSelectionApiResponse,
  AbandonedFlightPath,
  AbandonedFlightSelectionApiResponse,
  AbandonedHotelSelectionApiResponse,
  AbandonedPriceBreakersHotel,
  AbandonedSelection,
  AbandonedSelectionApiResponse,
  PriceWatchData,
  PriceWatchDataWithFlags,
  RecentSearchAndPriceWatchList,
  RecentTripSearches,
  RTLHotel,
  SearchProductDataFiltered,
  SopqHotel,
} from './types'

const DATE_FORMAT = 'E, MMM dd'

function getDates({ startDate, endDate = '' }: { startDate: string; endDate?: string | null }) {
  const formatStartDate = startDate.split('T')[0]
  const formatEndDate = endDate?.split('T')[0]
  // ONE-WAY
  if (!formatEndDate) {
    return [
      format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
      format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
    ]
  }
  return [
    format(parse(formatStartDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
    format(parse(formatEndDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
  ]
}

function formatDates({ startDate = '', endDate = '', dateFormat = DATE_FORMAT }) {
  return [
    format(parse(startDate, 'yyyyMMdd', new Date()), dateFormat),
    format(parse(endDate, 'yyyyMMdd', new Date()), dateFormat),
  ]
}

export function getRecentSearchDisplayProps(item: RecentTripSearches) {
  switch (item.productType) {
    case 'FLY': {
      const {
        travelStartDate,
        travelEndDate,
        listingsUrl,
        tripType,
        originAirportInfo,
        destinationAirportInfo,
      } = item

      const [startDate, endDate] = getDates({
        startDate: travelStartDate,
        endDate: travelEndDate,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      const {
        city: originCity,
        name: originName,
        state: originState,
        country: originCountry,
      } = originAirportInfo
      const {
        city: destinationCity,
        name: destinationName,
        state: destinationState,
        country: destinationCountry,
      } = destinationAirportInfo
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <FlightsFill size='24px' />
          </div>
        ),
        overline: 'Flight',
        heading: `${
          tripType === 'ROUND_TRIP' ? `${originCity || originName}, ${originState || originCountry} - ` : ``
        }${destinationCity || destinationName}, ${destinationState || destinationCountry}`,
        actionText: `${formattedStartDate}${item.tripType === 'ROUND_TRIP' ? ` - ${formattedEndDate}` : ``}`,
        relativeUrl: listingsUrl,
      }
    }
    case 'DRIVE': {
      const { pickupDateTime, returnDateTime, pickupLocationName, returnLocationName, listingsUrl } = item
      const [startDate, endDate] = getDates({
        startDate: pickupDateTime,
        endDate: returnDateTime,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <CarsFill size='24px' />
          </div>
        ),
        overline: 'Cars',
        heading: `${pickupLocationName}${
          pickupLocationName !== returnLocationName ? ` - ${returnLocationName}` : ''
        }`,
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        relativeUrl: listingsUrl,
      }
    }
    default: {
      const { cityName, checkInDate, checkOutDate, listingsUrl } = item
      const [startDate, endDate] = getDates({
        startDate: checkInDate,
        endDate: checkOutDate,
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate,
      })
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <HotelsFill size='24px' />
          </div>
        ),
        overline: 'Hotels',
        heading: cityName,
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        relativeUrl: listingsUrl,
      }
    }
  }
}

function transformAbandonedCarApiResponseToProps(item: AbandonedCarSelectionApiResponse) {
  const {
    averageRating,
    carURL,
    carType,
    carExample,
    numberOfReviews,
    pickupLocation,
    pickupDateTime,
    returnDateTime,
    isOffAirportPickup,
    isDropOffAtDifferentLocation,
    retailTypeCarCompanyDetails,
    passengerCapacity,
    returnLocation,
    imageURL,
    genericListingsUrl,
  } = item
  const parsedCheckInDate = parse(pickupDateTime, "yyyy-MM-dd'T'HH:mm:ss", new Date())
  const formattedCheckInDate = format(parsedCheckInDate, "EEE, MMM dd, yyyy 'at' h:mmaaa")
  const parsedCheckOutDate = parse(returnDateTime, "yyyy-MM-dd'T'HH:mm:ss", new Date())
  const formattedCheckOutDate = format(parsedCheckOutDate, "EEE, MMM dd, yyyy 'at' h:mmaaa")
  const pickUpDateInfo = {
    date: format(parsedCheckInDate, 'EEE, MMM dd, yyyy'),
    time: format(parsedCheckInDate, 'h:mmaaa'),
  }
  const dropOffDateInfo = {
    date: format(parsedCheckOutDate, 'EEE, MMM dd, yyyy'),
    time: format(parsedCheckOutDate, 'h:mmaaa'),
  }

  return {
    headline: carType,
    expressDealText: carExample,
    pickupLocation,
    isDropOffAtDifferentLocation,
    returnLocation,
    specifyOffAirport: isOffAirportPickup,
    pickUpDate: formattedCheckInDate,
    dropOffDate: formattedCheckOutDate,
    numAdults: passengerCapacity.toString(),
    numAdultsCount: passengerCapacity,
    linkUrl: carURL,
    genericListingsUrl,
    numReviews: numberOfReviews.toLocaleString(),
    numReviewsCount: numberOfReviews,
    overallRating: averageRating !== undefined ? String(averageRating) : undefined,
    overallRatingValue: averageRating,
    imageSrc: imageURL,
    rcDealType: 'RETAIL',
    productType: 'cars' as const,
    pickUpDateInfo,
    dropOffDateInfo,
    brandImg: retailTypeCarCompanyDetails?.brandImageURL
      ? `${retailTypeCarCompanyDetails.brandImageURL}&opto&auto=webp&width=176`
      : undefined,
  }
}

function isSopqHotel(hotelData: RTLHotel | SopqHotel | AbandonedPriceBreakersHotel): hotelData is SopqHotel {
  return hotelData.__typename === 'SopqHotel'
}

function isPriceBreaker(
  hotelData: RTLHotel | SopqHotel | AbandonedPriceBreakersHotel,
): hotelData is AbandonedPriceBreakersHotel {
  return hotelData.__typename === 'AbandonedPriceBreakersHotel'
}
function transformAbandonedHotelApiResponseToProps(item: AbandonedHotelSelectionApiResponse) {
  const {
    hotelURL,
    genericListingsUrl,
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfRooms,
    hotelTotalPriceWithTaxesAndFeesAtAbandonment,
    hotelTotalPriceExcludingTaxes,
    priceDisplayRegulation,
  } = item

  const parsedCheckInDate = parse(checkInDate, 'yyyy-MM-dd', new Date())
  const formattedCheckInDate = format(parsedCheckInDate, 'EEE, MMM dd, yyyy')
  const parsedCheckOutDate = parse(checkOutDate, 'yyyy-MM-dd', new Date())
  const formattedCheckOutDate = format(parsedCheckOutDate, 'EEE, MMM dd, yyyy')
  const commonDetails = {
    checkInDate: formattedCheckInDate,
    checkOutDate: formattedCheckOutDate,
    numAdults: numberOfAdults,
    numRooms: numberOfRooms,
    linkUrl: hotelURL,
    genericListingsUrl,
    productType: 'hotels' as const,
    hotelTotalPriceWithTaxesAndFeesAtAbandonment,
    hotelTotalPriceExcludingTaxes,
    priceDisplayRegulation,
  }
  const hotelData = item.hotelInfo

  if (isSopqHotel(hotelData)) {
    const { __typename, name, neighborhood, location } = hotelData

    const neighborhoodName = neighborhood?.name || ''
    const locationName = location?.name || ''
    const hotelDescription = name || ''
    return {
      ...commonDetails,
      hotelDealType: __typename,
      headline: `${hotelDescription} in ${neighborhoodName}`,
      subheadline: locationName,
    }
  }

  if (isPriceBreaker(hotelData)) {
    const { __typename, pricebreakerDescription, pricebreakerLocation } = hotelData
    return {
      ...commonDetails,
      hotelDealType: __typename,
      headline: pricebreakerDescription,
      subheadline: pricebreakerLocation,
    }
  }

  const { __typename, id, images, isFavorite, location, name, neighborhood, reviewInfo, starRating } =
    hotelData

  const imageSrc = resizeAndOptimizeImage(images[0].fastlyUrl)
  const rating = starRating || ''
  const totalReviews = reviewInfo?.reviewSummary?.total?.count || 0
  const overall = reviewInfo?.reviewSummary?.scores?.[0]?.score || ''
  const neighborhoodName = neighborhood?.name || ''

  return {
    ...commonDetails,
    hotelDealType: __typename,
    headline: name || '',
    subheadline: neighborhoodName,
    numReviews: totalReviews,
    numStars: parseInt(rating, 10),
    overallRating: parseFloat(overall),
    imageSrc,
    id,
    isFavorite,
    cityId: location?.id,
  }
}

function isTripDateValid(tripDate: string, formatString: string) {
  try {
    const parseTripDate = parse(tripDate, formatString, new Date())

    return isToday(parseTripDate) || isAfter(parseTripDate, new Date())
  } catch (err) {
    analytics.logError({
      message: 'Error parsing abandoned selection date',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })

    return false
  }
}

function isValidAbandonedHotelSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedHotelSelectionApiResponse {
  return (
    'hotelURL' in selection && isTripDateValid(selection.checkInDate, 'yyyy-MM-dd') && !selection.isBooked
  )
}

function isValidAbandonedCarSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedCarSelectionApiResponse {
  return (
    'carURL' in selection &&
    isTripDateValid(selection.pickupDateTime, "yyyy-MM-dd'T'HH:mm:ss") &&
    !selection.isBooked
  )
}

function isValidAbandonedFlightSelection(
  selection: AbandonedSelectionApiResponse,
): selection is AbandonedFlightSelectionApiResponse {
  return (
    'flightURL' in selection &&
    isTripDateValid(selection.originDestinationInfo.travelStartDateTime, "yyyy-MM-dd'T'HH:mm:ss") &&
    !selection.isBooked
  )
}

function getOriginDestAirportCodes(flightPath: AbandonedFlightPath[]) {
  return {
    departOriginAirportCode: flightPath[0].originAirportCode,
    departDestAirportCode: flightPath[0].destAirportCode,
    returnOriginAirportCode: flightPath[flightPath.length - 1].originAirportCode,
    returnDestAirportCode: flightPath[flightPath.length - 1].destAirportCode,
  }
}

export function transformAbandonedFlightApiResponseToProps(item: AbandonedFlightSelectionApiResponse) {
  const {
    travelStartDateTime,
    travelEndDateTime,
    originAirportInfo: {
      city: originCity = '',
      state: originState = '',
      country: originCountry,
      code: originCode,
    },
    destinationAirportInfo: {
      city: destCity = '',
      state: destState = '',
      country: destCountry,
      code: destCode,
    },
  } = item.originDestinationInfo
  const [startDate, endDate] = getDates({
    startDate: travelStartDateTime,
    endDate: travelEndDateTime,
  })
  const [formattedStartDate, formattedEndDate] = formatDates({
    startDate,
    endDate,
    dateFormat: 'E, MMM dd, yyyy',
  })
  const { departOriginAirportCode, departDestAirportCode, returnOriginAirportCode, returnDestAirportCode } =
    getOriginDestAirportCodes(item.originDestinationInfo.flightPath)
  return {
    headline: `${originCity}, ${
      originState || originCountry
    } (${originCode}) - ${destCity}, ${destState || destCountry} (${destCode})`,
    linkUrl: item.flightURL,
    genericListingsUrl: item.genericListingsUrl,
    productType: 'flights' as const,
    tripType: item.tripType,
    dealType: item.dealType,
    departDate: formattedStartDate,
    returnDate: formattedEndDate,
    departOriginAirportCode,
    departDestAirportCode,
    returnOriginAirportCode,
    returnDestAirportCode,
    numAdults: item.numberOfAdults,
  }
}

export function getFirstValidAbandonedSelection(
  selections: ReadonlyArray<AbandonedSelectionApiResponse>,
): AbandonedSelection | null {
  const abandonedSelection =
    selections.find((selection) => {
      if (isValidAbandonedHotelSelection(selection)) {
        return true
      }
      if (isValidAbandonedFlightSelection(selection)) {
        return true
      }

      return isValidAbandonedCarSelection(selection)
    }) ?? null

  if (abandonedSelection) {
    if ('hotelURL' in abandonedSelection) {
      return transformAbandonedHotelApiResponseToProps(abandonedSelection)
    }
    if ('flightURL' in abandonedSelection) {
      return transformAbandonedFlightApiResponseToProps(abandonedSelection)
    }

    return transformAbandonedCarApiResponseToProps(abandonedSelection)
  }

  return null
}

export function getPriceWatchProps(item: PriceWatchData) {
  const {
    originAbbreviation,
    originCityName,
    originStateName,
    destAbbreviation,
    destCityName,
    destStateName,
    dealPrice,
    savingsPercentage,
    departDate,
    returnDate,
    tripType,
    cabinClass,
  } = item
  const [startDate, endDate] = getDates({
    startDate: departDate,
    endDate: returnDate,
  })
  const [formattedStartDate, formattedEndDate] = formatDates({
    startDate,
    endDate,
  })
  const mappedFlyCabinClass = FLY_CABIN_CLASS[cabinClass]
  return {
    icon: (
      <div style={{ lineHeight: '0px' }}>
        <FlightsFill size='24px' />
      </div>
    ),
    heading: `${originCityName}, ${originStateName} - ${destCityName}, ${destStateName}`,
    actionText: `${formattedStartDate} - ${formattedEndDate}`,
    relativeUrl: `/m/fly/search/${originAbbreviation}-${destAbbreviation}-${startDate}${
      tripType === 'ROUND_TRIP' ? `/${destAbbreviation}-${originAbbreviation}-${endDate}` : ``
    }/?cabin-class=${mappedFlyCabinClass}&no-date-search=false&num-adults=1&sbsroute=slice1&search-type=10`,
    priceWatch: {
      dealPrice,
      savingsPercentage,
    },
  }
}

function buildPriceWatchSet(priceWatches: readonly PriceWatchData[]) {
  const mappedPriceWatches = priceWatches.map((item) => {
    const { departDate, returnDate, originAbbreviation, destAbbreviation } = item
    return `${departDate} - ${returnDate} - ${originAbbreviation} - ${destAbbreviation}`
  })
  return new Set(mappedPriceWatches)
}

export function computePriceWatchAndRecentSearches(
  priceWatches: readonly PriceWatchData[],
  recentSearchData: readonly RecentTripSearches[],
): RecentSearchAndPriceWatchList {
  const priceWatchSet = buildPriceWatchSet(priceWatches)
  const filteredRecentSearches =
    priceWatches.length === 0
      ? recentSearchData
      : recentSearchData.filter((item) => {
          if (item.productType === 'FLY') {
            const {
              travelStartDate,
              travelEndDate,
              originAirportInfo: { code: originCode },
              destinationAirportInfo: { code: destinationCode },
            } = item
            return !priceWatchSet.has(
              `${travelStartDate} - ${travelEndDate} - ${originCode} - ${destinationCode}`,
            )
          }
          return true
        })
  const priceWatchesWithFlag = priceWatches.map(
    (item) =>
      ({
        ...item,
        isPriceWatch: true,
        productType: 'FLY',
      }) as const,
  )
  return [...priceWatchesWithFlag, ...filteredRecentSearches]
}

function isTripISODateValid(tripDate: string) {
  try {
    const parseTripDate = parseISO(tripDate)
    return isToday(parseTripDate) || isAfter(parseTripDate, new Date())
  } catch (err) {
    analytics.logError({
      message: 'Error parsing trip grouping date',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })

    return false
  }
}

export function getFirstValidTripGrouping(groupings: ReadonlyArray<TripGroupingApiResponse>) {
  const tripGroup = groupings.find((grouping) =>
    isTripISODateValid(grouping.bookingActivityData[0]?.travelStartDateTime),
  )

  // in case of multiple bookings, show first booking in group
  const tripGroupingBooking = tripGroup?.bookingActivityData?.[0] ?? null

  const tripGroupingAbandonedSelection = getFirstValidAbandonedSelection(
    tripGroup?.selectionActivityData ?? [],
  )

  return { tripGroupingBooking, tripGroupingAbandonedSelection }
}

export function onCardClick(
  item: SearchProductDataFiltered | RecentTripSearches | PriceWatchDataWithFlags,
  relativeUrl: string,
  pageName: 'homepage' | 'landing',
  productType: 'FLY' | 'STAY' | 'DRIVE',
) {
  fireRecentSearchCardClickEvent(item, productType, pageName)
  const url = new URL(relativeUrl, window.location.origin)
  window.open(url)
}

export function onViewAllButtonClick(pageName: 'homepage' | 'landing') {
  fireViewAllButtonClickEvent(pageName)
  window.open('/next-profile/trips?tab=recent_activity')
}

export async function handleToggleFavorite(
  { hotelId, cityId, cguid, appName, appVersion }: FavoriteHotel,
  isNowFavorite: boolean,
) {
  if (!cguid) {
    return
  }

  try {
    await toggleFavoriteHotel(isNowFavorite ? 'add' : 'remove', {
      hotelId,
      cityId,
      cguid,
      appName,
      appVersion,
    })
  } catch (err) {
    analytics.logError({
      message: 'Error toggling favorite status',
      errorMessage: err instanceof Error ? err.message : String(err),
      logObject: {
        error: JSON.stringify(err),
      },
    })
  }
}
